import React, { useEffect, useState } from "react";
import { DeviceMobile, X, Share, Plus } from "@phosphor-icons/react";

const DISMISS_KEY = "bula_pwa_dismissed_at";
const DISMISS_DAYS = 14;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function dismissedRecently() {
  const v = localStorage.getItem(DISMISS_KEY);
  if (!v) return false;
  const days = (Date.now() - Number(v)) / (1000 * 60 * 60 * 24);
  return days < DISMISS_DAYS;
}

/**
 * Two flavours:
 *   • Android / Chrome / Edge — listen for `beforeinstallprompt`, then offer an Install button.
 *   • iOS Safari — no API exists, so show a banner explaining the Share → Add to Home Screen flow.
 */
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    if (isIOS()) setShowIos(true);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDeferred(null);
    setShowIos(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") dismiss();
    setDeferred(null);
  };

  if (!deferred && !showIos) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed left-3 right-3 bottom-3 md:left-auto md:right-6 md:bottom-6 md:w-[380px] z-50 animate-in slide-in-from-bottom-2"
    >
      <div className="rounded-2xl bg-[#0F2537] text-white shadow-2xl border border-white/10 overflow-hidden">
        <button
          onClick={dismiss}
          data-testid="pwa-dismiss"
          aria-label="Dismiss"
          className="absolute top-2 right-2 w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
        >
          <X size={14} weight="bold" />
        </button>
        <div className="p-4 pr-9 flex gap-3 items-start">
          <div className="w-11 h-11 rounded-xl bg-[#E05D3A] flex items-center justify-center shrink-0">
            <DeviceMobile size={22} weight="fill" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-heading font-black text-base leading-tight">Install Bula Golf</h3>
            {deferred ? (
              <>
                <p className="text-xs text-white/70 mt-1 leading-snug">
                  Add it to your home screen for instant access on the course — works like a native app.
                </p>
                <button
                  onClick={install}
                  data-testid="pwa-install-btn"
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E05D3A] text-white text-xs font-bold hover:brightness-110 transition"
                >
                  Install app
                </button>
              </>
            ) : (
              <p className="text-xs text-white/80 mt-1 leading-snug flex items-center flex-wrap gap-1" data-testid="pwa-ios-instructions">
                Tap <Share size={12} weight="fill" className="inline-block text-white" /> then
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white/10 font-semibold">
                  <Plus size={10} weight="bold" /> Add to Home Screen
                </span>
                to install.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
